/**
 * Inline news ticker for the main page.
 *
 * Renders the most recent ``news.event_ingested`` events as a single
 * horizontally-scrolling row.  Filters out duplicates the dedupe layer
 * already flagged so the same headline doesn't appear N times when
 * five wires print the same fact within the same minute.
 *
 * We feed off the existing Zustand ``feed`` slice rather than opening
 * a second WebSocket — the news events arrive through the same
 * ``DashboardStream`` as everything else, just with ``subject="news"``.
 */
import { useMemo } from 'react';

import type { AnyEnvelope } from '@/events';
import { useIsMobile } from '@/hooks/useBreakpoint';
import { useDashboardStore } from '@/state/store';

interface NewsItem {
  id: string;
  title: string;
  tickers: string[];
  sentiment: number;
  ts: string;
}

function isNewsEvent(env: AnyEnvelope): boolean {
  return env.type === 'news.event_ingested';
}

function toNewsItem(env: AnyEnvelope): NewsItem {
  const d = env.data as Record<string, unknown>;
  return {
    id: env.id,
    title: String(d.title ?? ''),
    tickers: (d.tickers as string[] | undefined) ?? [],
    sentiment: Number(d.sentiment ?? 0),
    ts: env.ts,
  };
}

function sentimentEmoji(value: number): string {
  if (value > 0.05) return '🟢';
  if (value < -0.05) return '🔴';
  return '🟡';
}

function sentimentColor(value: number): string {
  if (value > 0.05) return '#2ea043';
  if (value < -0.05) return '#f85149';
  return '#8b949e';
}

export function NewsTicker({ maxItems = 8 }: { maxItems?: number }) {
  const isMobile = useIsMobile();
  const feed = useDashboardStore((s) => s.feed);
  const items = useMemo(() => {
    const out: NewsItem[] = [];
    // Walk newest-first; bounded scan keeps render O(maxItems).  We
    // intentionally do NOT filter ``data.is_duplicate`` here -- in
    // production every replayed news event arrived with that flag
    // set, so the ticker stayed silently empty even with a fresh
    // headline in the feed.  Dedupe by ``event_id`` instead, which
    // collapses repeats without trusting the backend's verdict.
    const seenEventIds = new Set<string>();
    for (let i = feed.length - 1; i >= 0 && out.length < maxItems; i -= 1) {
      const env = feed[i];
      if (!env || !isNewsEvent(env)) continue;
      const d = env.data as Record<string, unknown>;
      const eventId = typeof d.event_id === 'string' ? d.event_id : env.id;
      if (seenEventIds.has(eventId)) continue;
      seenEventIds.add(eventId);
      out.push(toNewsItem(env));
    }
    return out;
  }, [feed, maxItems]);

  if (items.length === 0) {
    return null;
  }

  return (
    <div
      // Wrapper holds the gradient fade-out + the scrolling row.  We
      // need the gradient as a sibling overlay rather than a
      // background on the scroller itself so it doesn't move with the
      // content on scroll.
      style={{
        background: '#0d1117',
        borderBottom: '1px solid #21262d',
        position: 'relative',
      }}
      title="Live news feed from NewsIntelligence — last few non-duplicate headlines"
    >
      <div
        className="news-ticker-scroll"
        style={{
          padding: isMobile ? '6px 10px 6px 10px' : '8px 14px',
          display: 'flex',
          gap: isMobile ? 10 : 16,
          // Horizontal scroll, but the visible scrollbar is hidden via
          // the ``news-ticker-scroll`` class (in index.css) so a busy
          // ticker doesn't paint an ugly bar across the strip on
          // narrow viewports (mobile / split-screen).  Users can still
          // wheel/drag/swipe horizontally because ``overflow: auto``
          // stays in effect, just without the visual chrome.
          overflowX: 'auto',
          overflowY: 'hidden',
          // Mobile drag-momentum scroll: without this the
          // horizontal swipe on iOS Safari stutters at chip
          // boundaries since the browser tries to be helpful.
          WebkitOverflowScrolling: 'touch',
          fontSize: isMobile ? 11 : 12,
          alignItems: 'center',
          // Hard-stop the ticker from eating vertical space when items
          // overflow horizontally: scroll instead of wrapping.
          flexWrap: 'nowrap',
          // Padding-right reserves space for the gradient fade so the
          // last item never gets clipped behind it mid-readable.
          paddingRight: 28,
        }}
      >
      <span
        style={{
          color: '#8b949e',
          textTransform: 'uppercase',
          letterSpacing: 1,
          fontSize: 10,
          whiteSpace: 'nowrap',
          // Label must not be shrunk by flex when the row gets crowded —
          // otherwise it collapses to a glyph and the ticker looks empty.
          flexShrink: 0,
        }}
      >
        📰 News
      </span>
      {items.map((item) => {
        const color = sentimentColor(item.sentiment);
        const ticker = item.tickers[0] ?? '';
        return (
          <span
            key={item.id}
            title={`${item.title} (sentiment: ${item.sentiment.toFixed(2)})`}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              whiteSpace: 'nowrap',
              color: '#e6edf3',
              // Bug fix: without ``flexShrink: 0`` every item collapses to
              // zero width when there are several of them in the row, so
              // only the label was visible and the ticker looked broken.
              // Cap individual width but never let flex squeeze it to 0.
              flexShrink: 0,
              // Tighter on mobile so you can see more than one chip
              // at a time on a 375px viewport.
              maxWidth: isMobile ? 240 : 360,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            <span aria-hidden style={{ fontSize: 10, flexShrink: 0 }}>
              {sentimentEmoji(item.sentiment)}
            </span>
            {ticker ? (
              <span
                style={{
                  fontFamily: 'monospace',
                  color,
                  fontSize: 10,
                  padding: '1px 5px',
                  borderRadius: 8,
                  border: `1px solid ${color}`,
                  flexShrink: 0,
                }}
              >
                {ticker}
              </span>
            ) : null}
            <span
              style={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                maxWidth: isMobile ? 180 : 280,
              }}
            >
              {item.title}
            </span>
          </span>
        );
      })}
      </div>
      {/* Right-edge fade-out hint: a soft gradient overlay tells the
          user there's more content beyond the viewport without
          showing a noisy horizontal scrollbar.  ``pointer-events:
          none`` so scroll/drag still passes through to the row. */}
      <div
        aria-hidden
        style={{
          position: 'absolute',
          top: 0,
          right: 0,
          bottom: 0,
          width: 32,
          pointerEvents: 'none',
          background:
            'linear-gradient(to right, rgba(13, 17, 23, 0) 0%, rgba(13, 17, 23, 1) 90%)',
        }}
      />
    </div>
  );
}
